import type { Ref } from 'react';

import type { EquityOverview } from '#/api/generated/fINRAMarketDataAPI.schemas';
import { DetailExternalLink } from '#/components/detail/DetailExternalLink';
import { DetailPrintButton } from '#/components/detail/DetailPrintButton';
import { TickerPriceSummary } from '#/components/detail/TickerPriceSummary';
import { PageTitle } from '#/components/ui/PageTitle';
import { Ticker } from '#/components/ui/Ticker';
import { Updated } from '#/components/ui/Updated';
import { formatInteger } from '#/utils/format';
import { getEquityETFPriceChangeDisplay } from '#/utils/priceChange';

import { formatDetailExchangeLabel } from './exchangeLabel';

interface EquitySummarySectionProps {
  data: EquityOverview | null | undefined;
  id: string;
  priceSummaryRef?: Ref<HTMLDivElement>;
}

export function EquitySummarySection({
  data,
  id,
  priceSummaryRef,
}: EquitySummarySectionProps) {
  if (!data) {
    return null;
  }

  const symbol = data.symbol;
  const companyName = data.name ?? id;

  const { percentChange, valueChange, periodLabel } =
    getEquityETFPriceChangeDisplay(data);
  const valueChangeSuffix = ` ${periodLabel}`;

  return (
    <header id="summary" className="flow flow-20 scroll-mt-(--scroll-offset)">
      <div className="text-static-14 flex items-center gap-4">
        <Ticker symbol={symbol} />
        {data.exchange && (
          <>
            <span>on</span>
            {formatDetailExchangeLabel(data.exchange)}
          </>
        )}
      </div>

      <PageTitle flush>{companyName}</PageTitle>

      {data.last_refreshed_timestamp && (
        <Updated
          date={data.last_refreshed_timestamp}
          variant="time"
          className="flow-8"
        />
      )}

      {/* Ticker Summary */}
      <div className="flex flex-col items-start justify-center gap-16 md:flex-row md:items-center md:justify-between">
        <TickerPriceSummary
          ref={priceSummaryRef}
          price={data.current_price}
          percentChange={percentChange}
          valueChange={valueChange}
          valueChangeSuffix={valueChangeSuffix}
        />
        <DetailPrintButton
          dataTrack="detail_download_pdf"
          dataTrackContext="equity_detail"
        />
      </div>

      {(data.company_description || data.website_url) && (
        <div className="flow flow-inner-8 text-fluid-base-sm">
          {data.company_description && (
            <p data-testid="company-description">{data.company_description}</p>
          )}
          {data.website_url && (
            <DetailExternalLink
              href={data.website_url}
              dataTrack="detail_company_website"
              dataTrackLabel="Company Website"
              dataTrackContext="equity_summary"
            >
              Company Website
            </DetailExternalLink>
          )}
        </div>
      )}

      <div className="text-static-14 border-blue-30 grid grid-cols-2 gap-y-20 border-t pt-20 md:grid-cols-4">
        <div>
          <p className="font-semibold text-blue-100">CEO</p>
          <p>{data.ceo_name ?? '—'}</p>
        </div>
        <div>
          <p className="font-semibold text-blue-100">Founded In</p>
          <p>{data.founded_year ?? '—'}</p>
        </div>
        <div>
          <p className="font-semibold text-blue-100">Headquarters</p>
          <p>{data.headquarters_location ?? '—'}</p>
        </div>
        <div>
          <p className="font-semibold text-blue-100">Employees</p>
          <p>{formatInteger(data.num_employees) ?? '—'}</p>
          {data.employee_number_as_of_date && (
            <div className="col-span-full mt-2">
              <Updated date={data.employee_number_as_of_date} variant="date" />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
